var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { Profiler, useEffect, useMemo, useState } from 'react';
import { scaleLinear } from 'd3';
import { getNewSpanColor, parseParams } from '@parca/functions';
import useUIFeatureFlag from '@parca/functions/useUIFeatureFlag';
import { Button, Card, useParcaContext } from '@parca/components';
import { useContainerDimensions } from '@parca/dynamicsize';
import { useAppSelector, selectDarkMode, selectSearchNodeString, selectFilterByFunction, useAppDispatch, setSearchNodeString, } from '@parca/store';
import { Callgraph } from '../';
import ProfileShareButton from '../components/ProfileShareButton';
import FilterByFunctionButton from './FilterByFunctionButton';
import ProfileIcicleGraph from '../ProfileIcicleGraph';
import TopTable from '../TopTable';
import useDelayedLoader from '../useDelayedLoader';
import '../ProfileView.styles.css';
function arrayEquals(a, b) {
    return (Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every(function (val, index) { return val === b[index]; }));
}
export var useProfileVisState = function () {
    var _a = useState(function () {
        if (typeof window === 'undefined') {
            return 'icicle';
        }
        var router = parseParams(window.location.search);
        var currentViewFromURL = router.currentProfileView;
        if (currentViewFromURL != null) {
            return currentViewFromURL;
        }
        return 'icicle';
    }), currentView = _a[0], setCurrentView = _a[1];
    return { currentView: currentView, setCurrentView: setCurrentView };
};
export var ProfileView = function (_a) {
    var flamegraphData = _a.flamegraphData, topTableData = _a.topTableData, callgraphData = _a.callgraphData, sampleUnit = _a.sampleUnit, profileSource = _a.profileSource, queryClient = _a.queryClient, navigateTo = _a.navigateTo, profileVisState = _a.profileVisState, onDownloadPProf = _a.onDownloadPProf;
    var dispatch = useAppDispatch();
    var _b = useContainerDimensions(), ref = _b.ref, dimensions = _b.dimensions;
    var _c = useState([]), curPath = _c[0], setCurPath = _c[1];
    var currentView = profileVisState.currentView, setCurrentView = profileVisState.setCurrentView;
    var isDarkMode = useAppSelector(selectDarkMode);
    var currentSearchString = useAppSelector(selectSearchNodeString);
    var filterByFunctionString = useAppSelector(selectFilterByFunction);
    var callgraphEnabled = useUIFeatureFlag('callgraph')[0];
    var highlightAfterFilteringEnabled = useUIFeatureFlag('highlightAfterFiltering')[0];
    var _d = useParcaContext(), loader = _d.loader, perf = _d.perf;
    useEffect(function () {
        // Reset the current path when the profile source changes
        setCurPath([]);
    }, [profileSource]);
    var isLoading = useMemo(function () {
        if (currentView === 'icicle') {
            return Boolean(flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.loading);
        }
        if (currentView === 'callgraph') {
            return Boolean(callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.loading);
        }
        if (currentView === 'table') {
            return Boolean(topTableData === null || topTableData === void 0 ? void 0 : topTableData.loading);
        }
        if (currentView === 'both') {
            return Boolean(flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.loading) || Boolean(topTableData === null || topTableData === void 0 ? void 0 : topTableData.loading);
        }
        return false;
    }, [currentView, callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.loading, flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.loading, topTableData === null || topTableData === void 0 ? void 0 : topTableData.loading]);
    useEffect(function () {
        if (!highlightAfterFilteringEnabled) {
            if (currentSearchString !== undefined && currentSearchString !== '') {
                dispatch(setSearchNodeString(''));
            }
            return;
        }
        if (isLoading) {
            return;
        }
        if (filterByFunctionString === currentSearchString) {
            return;
        }
        dispatch(setSearchNodeString(filterByFunctionString));
    }, [
        isLoading,
        filterByFunctionString,
        dispatch,
        highlightAfterFilteringEnabled,
        currentSearchString,
    ]);
    var isLoaderVisible = useDelayedLoader(isLoading);
    if ((flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error) != null) {
        console.error('Error: ', flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error);
        return (_jsxs("div", __assign({ className: "p-10 flex justify-center" }, { children: ["An error occurred: ", flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error.message] })));
    }
    var resetIcicleGraph = function () { return setCurPath([]); };
    var setNewCurPath = function (path) {
        if (!arrayEquals(curPath, path)) {
            setCurPath(path);
        }
    };
    var switchProfileView = function (view) {
        if (view == null) {
            return;
        }
        setCurrentView(view);
        if (navigateTo === undefined) {
            return;
        }
        var router = parseParams(window.location.search);
        navigateTo('/', __assign(__assign(__assign({}, router), { currentProfileView: view }), { searchString: currentSearchString }));
    };
    var maxColor = getNewSpanColor(isDarkMode);
    // TODO: fix colors for dark mode
    var minColor = scaleLinear([isDarkMode ? 'black' : 'white', maxColor])(0.3);
    var colorRange = [minColor, maxColor];
    return (_jsx(_Fragment, { children: _jsx("div", __assign({ className: "py-3" }, { children: _jsx(Card, { children: _jsxs(Card.Body, { children: [_jsxs("div", __assign({ className: "flex py-3 w-full" }, { children: [_jsxs("div", __assign({ className: "w-2/5 flex space-x-4" }, { children: [_jsxs("div", __assign({ className: "flex space-x-1" }, { children: [profileSource != null && queryClient != null ? (_jsx(ProfileShareButton, { queryRequest: profileSource.QueryRequest(), queryClient: queryClient, disabled: isLoading })) : null, _jsx(Button, __assign({ color: "neutral", onClick: function (e) {
                                                        e.preventDefault();
                                                        onDownloadPProf();
                                                    }, disabled: isLoading }, { children: "Download pprof" }))] })), _jsx(FilterByFunctionButton, {})] })), _jsxs("div", __assign({ className: "flex ml-auto gap-2" }, { children: [_jsx(Button, __assign({ color: "neutral", onClick: resetIcicleGraph, disabled: curPath.length === 0, className: "whitespace-nowrap text-ellipsis" }, { children: "Reset View" })), callgraphEnabled ? (_jsx(Button, __assign({ variant: "".concat(currentView === 'callgraph' ? 'primary' : 'neutral'), onClick: function () { return switchProfileView('callgraph'); }, className: "whitespace-nowrap text-ellipsis" }, { children: "Callgraph" }))) : null, _jsxs("div", __assign({ className: "flex" }, { children: [_jsx(Button, __assign({ variant: "".concat(currentView === 'table' ? 'primary' : 'neutral'), className: "items-center rounded-tr-none rounded-br-none w-auto px-8 whitespace-nowrap text-ellipsis no-outline-on-buttons", onClick: function () { return switchProfileView('table'); } }, { children: "Table" })), _jsx(Button, __assign({ variant: "".concat(currentView === 'both' ? 'primary' : 'neutral'), className: "items-center rounded-tl-none rounded-tr-none rounded-bl-none rounded-br-none border-l-0 border-r-0 w-auto px-8 whitespace-nowrap no-outline-on-buttons text-ellipsis", onClick: function () { return switchProfileView('both'); } }, { children: "Both" })), _jsx(Button, __assign({ variant: "".concat(currentView === 'icicle' ? 'primary' : 'neutral'), className: "items-center rounded-tl-none rounded-bl-none w-auto px-8 whitespace-nowrap text-ellipsis no-outline-on-buttons", onClick: function () { return switchProfileView('icicle'); } }, { children: "Icicle Graph" }))] }))] }))] })), isLoaderVisible ? (_jsx(_Fragment, { children: loader })) : (_jsxs("div", __assign({ ref: ref, className: "flex space-x-4 justify-between w-full" }, { children: [currentView === 'icicle' && (flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.data) != null && (_jsx("div", __assign({ className: "w-full" }, { children: _jsx(Profiler, __assign({ id: "icicleGraph", onRender: perf === null || perf === void 0 ? void 0 : perf.onRender }, { children: _jsx(ProfileIcicleGraph, { curPath: curPath, setNewCurPath: setNewCurPath, graph: flamegraphData.data, sampleUnit: sampleUnit }) })) }))), currentView === 'callgraph' && (callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.data) != null && (_jsx("div", __assign({ className: "w-full" }, { children: (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) !== undefined && (_jsx(Callgraph, { graph: callgraphData.data, sampleUnit: sampleUnit, width: dimensions === null || dimensions === void 0 ? void 0 : dimensions.width, colorRange: colorRange })) }))), currentView === 'table' && topTableData != null && (_jsx("div", __assign({ className: "w-full" }, { children: _jsx(TopTable, { data: topTableData.data, sampleUnit: sampleUnit }) }))), currentView === 'both' && (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: "w-1/2" }, { children: _jsx(TopTable, { data: topTableData === null || topTableData === void 0 ? void 0 : topTableData.data, sampleUnit: sampleUnit }) })), _jsx("div", __assign({ className: "w-1/2" }, { children: flamegraphData != null && (_jsx(ProfileIcicleGraph, { curPath: curPath, setNewCurPath: setNewCurPath, graph: flamegraphData.data, sampleUnit: sampleUnit })) }))] }))] })))] }) }) })) }));
};
